/**
 * Channels Sync Policy
 *
 * Subscribes to ExternalSystemsIntegrations and filters for sales channels.
 * Dispatches upsert/remove commands when channel data changes.
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType, ReadmodelChange } from '@bett3r-dev/teselly-client-library';
import { SystemIntegrationConnectorReadmodel, SystemIntegrationsReadmodel } from '@bett3r-dev/teselly-domain';
import { ChannelsState } from '../state';
import { ChannelsCommand } from '../commands';

/**
 * Creates a policy that syncs channels from backend subscription
 */
export const createChannelsSyncPolicy = (
  backend: ClientLibraryType,
  getConnectors: () => Record<string, SystemIntegrationConnectorReadmodel>
): Policy<ChannelsState, ChannelsCommand> => ({
  name: 'channels-sync',

  init: ({ dispatch }) => {
    dispatch(
      { type: 'integrations/channels/loading/setChannels', payload: { isLoading: true }},
      { source: 'api' }
    );

    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    // The connector registry hydrates asynchronously; the snapshot can arrive
    // before it is populated. Retry until connectors are available (bounded) so
    // the sales-channel filter doesn't drop every channel on a cold load.
    const applySnapshot = (
      items: SystemIntegrationsReadmodel[],
      attempt: number = 0
    ): void => {
      const connectors = getConnectors();
      const MAX_RETRY = 30;
      const RETRY_MS = 100;

      if ( Object.keys( connectors ).length === 0 && attempt < MAX_RETRY ) {
        retryTimer = setTimeout(() => {
          retryTimer = null;
          applySnapshot( items, attempt + 1 );
        }, RETRY_MS );
        return;
      }

      const channelItems = items.filter( item => {
        const connector = connectors[item.connectorId];
        return connector?.type === 'salesChannel';
      });

      const channelsRecord = channelItems.reduce<Record<string, SystemIntegrationsReadmodel>>(
        ( acc, channel ) => {
          acc[channel.id] = channel;
          return acc;
        },
        {}
      );
      dispatch(
        { type: 'integrations/channels/data/setAll', payload: { channels: channelsRecord }},
        { source: 'api' }
      );
    };

    const subscription = backend.subscriptions.ExternalSystemsIntegrations(
      {},
      ( change: ReadmodelChange<SystemIntegrationsReadmodel> ) => {
        const connectors = getConnectors();

        if ( change.type === 'snapshot' ) {
          applySnapshot( change.data.items );
          return;
        }

        if ( change.type === 'signal' ) return;

        const record = change.type === 'delete' ? change.previous : change.data;
        const connector = connectors[record.connectorId];
        if ( connector?.type !== 'salesChannel' ) return;

        if ( change.type === 'delete' ) {
          dispatch(
            { type: 'integrations/channels/data/remove', payload: { integrationId: change.previous.id }},
            { source: 'api' }
          );
        } else {
          dispatch(
            { type: 'integrations/channels/data/upsert', payload: { channel: change.data }},
            { source: 'api' }
          );
        }
      }
    );

    setTimeout(() => {
      dispatch(
        { type: 'integrations/channels/loading/setChannels', payload: { isLoading: false }},
        { source: 'api' }
      );
    }, 500 );

    return () => {
      if ( retryTimer !== null ) {
        clearTimeout( retryTimer );
        retryTimer = null;
      }
      subscription.close();
    };
  }
});
